// components/CurrentIdentity/CurrentIdentity.jsx
import React, { useState, useContext, useEffect, useCallback, useRef } from "react";
import { UserContext } from "../../../../../UserContext";
// import WidgetContainer from "../WidgetContainer";
import CurrentYou from "../Identity/CurrentYou/CurrentYou.jsx";
import FutureIdentity from "../Identity/FutureIdentity.jsx";
import currentIdentityAvatar from "../../../../../assets/currentIdentityAvatar.png";
import currentIdentityAvatar2 from "../../../../../assets/currentIdentityAvatar2.png";
import { progressService } from "../../../../../services/progressService";

const CurrentIdentity = ({ userId, courseId, onSubsectionChange, onPhaseChange, subsectionId, actions, initialData, course }) => {
  const [step, setStep] = useState(1);
  const [currentYouReady, setCurrentYouReady] = useState(false);
  const { user } = useContext(UserContext);
  const hasMountedRef = useRef(false);
  const lastSavedStepRef = useRef(step);

  const userName =
    user?.firstName && user?.lastName
      ? `${user.firstName} ${user.lastName}`
      : user?.firstName || user?.lastName || "";

  // IDs for navigation within the Identity flow
  const currentYouSubsectionId = "f90552a1-12fd-4f0b-b4f5-17e258b84b97";
  const futureYouSubsectionId = "a6f1a312-7580-4988-8cc2-64d342be7ab3";
  // Set this to the actual next subsection after Future Identity
  const nextAfterFutureSubsectionId = null; // TODO: provide real ID for the next section (e.g., Values)

  const handleNext = useCallback(() => {
    setStep((prev) => {
      const nextStep = prev + 1;
      if (nextStep === 3) {
        // Move to Future Identity subsection
        onSubsectionChange?.(futureYouSubsectionId);
      } else if (nextStep === 4) {
        // Completed Future Identity; navigate explicitly to the next subsection if provided
        if (nextAfterFutureSubsectionId) {
          onSubsectionChange?.(nextAfterFutureSubsectionId);
        }
      }
      return nextStep;
    });
  }, [onSubsectionChange]);

  // Report phase for ModuleSidebar visibility
  useEffect(() => {
    if (!onPhaseChange) return;
    if (step === 1) onPhaseChange("intro");
    else onPhaseChange("immersive");
  }, [step, onPhaseChange]);

  // Configure primary action in SubsectionContent
useEffect(() => {
  if (!actions) return;

  const visible = step !== 1 && (step !== 2 || currentYouReady);

  actions.setPrimary({
    label: "Next Step",
    onClick: handleNext,
    disabled: false,
    loading: false,
    visible,
  });

  return () => actions.clearPrimary?.();
  }, [step, actions, currentYouReady, handleNext]);


  // Save step as in-progress for this subsection
  useEffect(() => {
    if (!userId || !subsectionId) return;

    // Skip the initial mount so we don't POST on first render
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      lastSavedStepRef.current = step;
      return;
    }

    // Only persist when the step actually changes
    if (step === lastSavedStepRef.current) return;

    // Merge the step flag with any existing payload instead of overwriting it
    const persistStep = async () => {
      try {
        const existing = await progressService.getUserProgress(userId, subsectionId);
        const existingData = existing?.data || {};
        if (!existing || !existingData) return; // don't create an empty record

        // Normalize payload: if data is nested (type + payload inside payload), unwrap to a single payload object
        const normalizedPayload =
          existingData.payload?.payload // payload wrapped twice
            ? existingData.payload.payload
            : existingData.payload || existingData;

        // If there's nothing meaningful yet, skip writing a blank payload
        if (!normalizedPayload || Object.keys(normalizedPayload).length === 0) return;

        const mergedPayload = { ...normalizedPayload, step };
        const mergedData = { type: "widget", payload: mergedPayload };

        await progressService.saveProgress(userId, subsectionId, mergedData, "in_progress");
        lastSavedStepRef.current = step;
      } catch (err) {
        console.error("Failed to persist step progress", err);
      }
    };

    persistStep();
  }, [step, userId, subsectionId]);

  

  return (
    <>
      {step === 1 && (
    
      <div>
          <div className="rounded-2xl bg-white p-6 md:p-8 mb-6">
          <h2 className="text-xl md:text-2xl lg:text-[32px] border-b pb-3 font-medium font-gotham text-[#323232] mb-6 mr-[-2rem]">
            Your Current Identity
          </h2>
            <p className="text-[#323232] font-gotham text-base md:text-lg leading-relaxed mb-2">
              The journey of career progression begins with a clear understanding of who you are today—your strengths,
              values, professional experiences, and aspirations. This stage invites you to pause and reflect on the
              unique qualities and achievements that define your current identity as a professional.
            </p>
            <p className="text-[#323232] font-gotham text-base md:text-lg leading-relaxed mb-2">
              By assessing your present state with honesty and intention, you build the foundation for meaningful growth.
            </p>
          </div>

          <div className="flex flex-col md:flex-row items-center justify-center gap-6 lg:gap-8 mb-8 px-0">
            {/* Current card */}
            <div className="w-48 sm:w-52 md:w-56 lg:w-64 relative flex-shrink-0">
              {/* Top */}
              <div className="bg-[#0D1111] h-24 sm:h-28 md:h-36 rounded-t-2xl flex items-start justify-center pt-2 sm:pt-3">
                <span className="text-white text-[9px] sm:text-[10px] md:text-[14px] font-semibold font-gotham tracking-widest">
                  CURRENT STATE
                </span>
              </div>

              {/* Bottom */}
              <div className="bg-white h-24 sm:h-28 md:h-36 rounded-b-2xl shadow-md" />

              {/* Avatar + Name */}
              <div className="absolute inset-x-0 top-1/2 mt-5 -translate-y-1/2 flex flex-col items-center">
                <div className="w-20 h-20 sm:w-22 sm:h-22 md:w-24 md:h-24 lg:w-32 lg:h-32 bg-[#E8E8E8] flex items-center justify-center rounded-full shadow-lg border-4 border-white">
                  <img
                    src={currentIdentityAvatar}
                    alt="Current Avatar"
                    className="w-full h-full object-contain rounded-full"
                  />
                </div>

                <div className="text-center mt-2 sm:mt-3 whitespace-nowrap">
                  <h3 className="text-xs sm:text-sm md:text-2xl font-medium font-gotham text-[#1C1C1C]">
                    {userName}
                  </h3>
                  <p className="text-[10px] sm:text-xs md:text-base font-medium font-gotham text-[#1C1C1C] mt-0.5">
                    {new Date()
                      .toLocaleString("en-US", { month: "short", year: "numeric" })
                      .toUpperCase()}
                  </p>
                </div>
              </div>
            </div>


            {/* Connector */}
            {/* <div className="flex md:flex-row flex-col items-center w-full h-full px-6">
              <div className="w-3 h-3 rounded-full bg-[#B2B2B2]" />
              <div className="flex-1 border-[#B2B2B2] border-dashed md:border-t md:border-l-0 border-l-4 md:h-0 h-full" />
              <div className="w-3 h-3 rounded-full bg-[#B2B2B2]" />
            </div> */}

            <div className="flex flex-col items-center justify-center">
                <svg
                  width="150"
                  height="10"
                  viewBox="0 0 130 10"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="4" cy="5" r="4" fill="#B2B2B2" />
                  <line
                    x1="4"
                    y1="5"
                    x2="200"
                    y2="5"
                    stroke="#B2B2B2"
                    strokeWidth="2"
                    strokeDasharray="13 13"
                  />
                  <circle cx="136" cy="5" r="4" fill="#B2B2B2" />
                </svg>
              </div>

            {/* Future card (dimmed) */}
            <div className="w-48 sm:w-52 md:w-56 lg:w-64 relative opacity-60 flex-shrink-0">
              {/* Top */}
              <div className="bg-[#CFEDEA] h-24 sm:h-28 md:h-36 rounded-t-2xl flex items-start justify-center pt-2 sm:pt-3">
                <span className="text-[9px] sm:text-[10px] md:text-[14px] font-semibold text-white font-gotham tracking-widest">
                  FUTURE STATE
                </span>
              </div>

              {/* Bottom */}
              <div className="bg-white h-24 sm:h-28 md:h-36 rounded-b-2xl shadow-sm" />

              {/* Avatar + Name */}
              <div className="absolute inset-x-0 top-1/2 mt-5 -translate-y-1/2 flex flex-col items-center">
                <div className="p-1.5 w-20 h-20 sm:w-22 sm:h-22 md:w-24 md:h-24 lg:w-32 lg:h-32 bg-[#E8E8E8] flex items-center justify-center rounded-full shadow-lg border-4 border-white">
                  <img
                    src={currentIdentityAvatar2}
                    alt="Future Avatar"
                    className="w-full h-full object-contain rounded-full"
                  />
                </div>

                <div className="text-center mt-2 sm:mt-3 whitespace-nowrap">
                  <h3 className="font-semibold text-xs sm:text-sm md:text-2xl font-gotham text-[#1C1C1C]">
                    {userName}
                  </h3>
                  <p className="text-[10px] sm:text-xs md:text-base font-medium font-gotham text-[#1C1C1C] mt-0.5">
                    2026 &amp; BEYOND
                  </p>
                </div>
              </div>
            </div>

          </div>
          <div className="mt-6 pt-6 border-t border-gray-100 flex justify-end">
            <button
              type="button"
              onClick={handleNext}
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-white font-medium font-gotham bg-[#54B0AF] hover:bg-teal-600"
            >
              Next Step
            </button>
          </div>
    </div>
      )}

      {step === 2 && (
        <CurrentYou
          subsectionId={subsectionId}
          legacySubsectionId={currentYouSubsectionId}
          initialData={initialData}
          onReadyChange={setCurrentYouReady}
        />
      )}

      {step === 3 && (
        <FutureIdentity
          userId={userId}
          subsectionId={futureYouSubsectionId}
          onComplete={handleNext}
          onPhaseChange={onPhaseChange}
          onSubsectionChange={onSubsectionChange}
          actions={actions}
          course={course}
        />
      )}
    </>
  );
};

export default CurrentIdentity;
